import type { RGB } from '../types';
import { rgbToOklch, oklchToRgb, rgbToHex } from './colors';
import { getContrastRatio } from './colorAnalysis';

/** Find a passing sRGB foreground near the original OKLCH lightness.
 * Tries both directions. Gamut clipping can change hue/chroma; recheck the final RGB.
 * If the target is impossible against this background, return the original and report it.
 */
export const fixContrastOklch = (fg: RGB, bg: RGB, targetRatio = 4.5): { fixed: RGB; wasAdjusted: boolean; meetsTarget: boolean } => {
  if (!Number.isFinite(targetRatio) || targetRatio < 1 || targetRatio > 21) throw new RangeError('Contrast target must be between 1 and 21');
  const toHex = (c: RGB) => rgbToHex(c.r, c.g, c.b);
  const bgHex = toHex(bg);
  const passes = (c: RGB) => getContrastRatio(toHex(c), bgHex) >= targetRatio;
  if (passes(fg)) return { fixed: fg, wasAdjusted: false, meetsTarget: true };
  const original = rgbToOklch(fg.r, fg.g, fg.b);
  const candidates: RGB[] = [];
  // colors.ts stores L on 0..100 and chroma as raw C * 100.
  for (const endpoint of [0, 100]) {
    const edge = oklchToRgb(endpoint, original.c, original.h);
    if (!passes(edge)) continue;
    let passing = endpoint;
    let failing = original.l;
    let best = edge;
    for (let i = 0; i < 32; i++) {
      const mid = (passing + failing) / 2;
      const candidate = oklchToRgb(mid, original.c, original.h);
      if (passes(candidate)) { best = candidate; passing = mid; }
      else failing = mid;
    }
    candidates.push(best);
  }
  // Exact black/white are fallback options when constant-chroma clipping cannot reach an endpoint.
  for (const c of [{ r: 0, g: 0, b: 0 }, { r: 255, g: 255, b: 255 }]) if (passes(c)) candidates.push(c);
  candidates.sort((a, b) => {
    const distance = (c: RGB) => {
      const o = rgbToOklch(c.r, c.g, c.b);
      const angle = (o.h - original.h) * Math.PI / 180;
      return (o.l - original.l) ** 2 + o.c ** 2 + original.c ** 2 - 2 * o.c * original.c * Math.cos(angle);
    };
    return distance(a) - distance(b);
  });
  const fixed = candidates[0] || fg;
  return { fixed, wasAdjusted: toHex(fixed) !== toHex(fg), meetsTarget: passes(fixed) };
};

/**
 * Build an NxN contrast matrix for a list of colors.
 * Each cell contains WCAG ratio and pass/fail for AA normal text (4.5:1).
 */
export interface ContrastCell {
  ratio: number;
  passAA: boolean;
  passAAA: boolean;
  passAALarge: boolean;
}

export const buildContrastMatrix = (hexColors: string[]): ContrastCell[][] => {
  const n = hexColors.length;
  const matrix: ContrastCell[][] = [];

  for (let i = 0; i < n; i++) {
    const row: ContrastCell[] = [];
    for (let j = 0; j < n; j++) {
      if (i === j) {
        row.push({ ratio: 1, passAA: false, passAAA: false, passAALarge: false });
      } else {
        const ratio = getContrastRatio(hexColors[i], hexColors[j]);
        row.push({
          ratio,
          passAA: ratio >= 4.5,
          passAAA: ratio >= 7,
          passAALarge: ratio >= 3,
        });
      }
    }
    matrix.push(row);
  }

  return matrix;
};
